import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, MapPin, Clock, Loader2, ChevronRight, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
    cv_file: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      // First try to seed the default job
      await axios.post(`${API}/seed/job`).catch(() => {});
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setApplicationForm({ ...applicationForm, cv_file: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!applicationForm.name || !applicationForm.email || !applicationForm.mobile) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/job-applications`, {
        ...applicationForm,
        job_id: selectedJob.id,
        job_title: selectedJob.title
      });
      toast.success('Application submitted! We will contact you soon.');
      setApplicationForm({
        name: '',
        email: '',
        mobile: '',
        message: '',
        cv_file: null
      });
      setDialogOpen(false);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 lg:pt-20" data-testid="careers-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-rust to-brand-orange py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Briefcase className="h-8 w-8" />
            <h1 className="font-heading text-4xl lg:text-5xl font-bold">
              Careers
            </h1>
          </div>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Join our team and be part of building dreams in the Jamul-Bhilai region
          </p>
        </div>
      </section>

      {/* Job Listings */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
            Current Openings
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
            </div>
          ) : jobs.length > 0 ? (
            <div className="space-y-6">
              {jobs.map((job) => (
                <Card key={job.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`job-card-${job.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1 text-brand-orange" />
                            {job.location}
                          </div>
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-1 text-brand-orange" />
                            {job.job_type}
                          </div>
                          {job.timing && (
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1 text-brand-orange" />
                              {job.timing}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleApply(job)}
                        className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full"
                        data-testid={`apply-btn-${job.id}`}
                      >
                        Apply Now
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>

                    {/* Job Details Expandable */}
                    <div className="mt-6 pt-6 border-t">
                      <p className="text-gray-600 mb-4">{job.description}</p>

                      {job.responsibilities && job.responsibilities.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Responsibilities:</h4>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {job.responsibilities.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {job.requirements && job.requirements.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Requirements:</h4>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            {job.requirements.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-2xl">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                No openings at the moment
              </h3>
              <p className="text-gray-500">
                Check back later or send your resume to{' '}
                <a href="mailto:contact@shreebajrangdevelopers.com" className="text-brand-orange hover:underline">
                  contact@shreebajrangdevelopers.com
                </a>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="apply-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Apply for {selectedJob?.title}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="app-name">Name *</Label>
              <Input
                id="app-name"
                value={applicationForm.name}
                onChange={(e) => setApplicationForm({ ...applicationForm, name: e.target.value })}
                placeholder="Your full name"
                required
                data-testid="apply-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-email">Email *</Label>
              <Input
                id="app-email"
                type="email"
                value={applicationForm.email}
                onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                placeholder="Your email"
                required
                data-testid="apply-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-mobile">Mobile *</Label>
              <Input
                id="app-mobile"
                type="tel"
                value={applicationForm.mobile}
                onChange={(e) => setApplicationForm({ ...applicationForm, mobile: e.target.value })}
                placeholder="Your mobile number"
                required
                data-testid="apply-mobile-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-cv">Upload CV</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="app-cv"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="flex-1"
                  data-testid="apply-cv-input"
                />
              </div>
              <p className="text-xs text-gray-500">PDF, DOC, DOCX (Max 5MB)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-message">Message</Label>
              <Textarea
                id="app-message"
                value={applicationForm.message}
                onChange={(e) => setApplicationForm({ ...applicationForm, message: e.target.value })}
                placeholder="Tell us about yourself..."
                rows={3}
                data-testid="apply-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full"
              data-testid="apply-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Why Work With Us */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8 text-center">
            Why Work With Us
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Local Impact', desc: 'Be part of developing the Jamul-Bhilai region and helping families find their dream homes' },
              { title: 'Growth Opportunities', desc: 'Learn and grow with a dynamic team in the real estate industry' },
              { title: 'Work-Life Balance', desc: 'Enjoy a supportive work environment with regular hours and weekends off' }
            ].map((item, index) => (
              <Card key={index}>
                <CardContent className="p-6 text-center">
                  <h3 className="font-heading font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
